import clsx from 'clsx';
import {
  type ElementType,
  forwardRef,
  type KeyboardEventHandler,
  type MouseEventHandler,
  type PointerEventHandler,
  type ReactElement,
  useState,
} from 'react';
import { useIntl } from 'react-intl';

import { ProgressCircle } from '../../content/progress/ProgressCircle.js';
import { useAriaLabelingProps } from '../../core/hooks/useAriaLabelingProps.js';
import { useBehavioralTrackingProps } from '../../core/hooks/useBehavioralTrackingProps.js';
import { useFocusRing } from '../../core/hooks/useFocusRing.js';
import { useSlots } from '../../core/hooks/useSlots.js';
import type {
  AriaDisabledProps,
  AriaLabelingProps,
} from '../../core/types/a11y-props.js';
import type { BehaviorTrackingProps } from '../../core/types/behavior-tracking-props.js';
import type { DataTestId } from '../../core/types/data-props.js';
import type { MaskingProps } from '../../core/types/masking-props.js';
import { type PolymorphicComponentProps } from '../../core/types/polymorph.js';
import type { StylingProps } from '../../core/types/styling-props.js';
import type { WithChildren } from '../../core/types/with-children.js';
import { mergeProps } from '../../core/utils/merge-props.js';
import { parseBoolean } from '../../core/utils/parse-boolean.js';
import * as styles from './InlineButton.sty.js';
import { Prefix } from './Prefix.js';
import { Suffix } from './Suffix.js';

const COMPONENT_NAME = 'InlineButton';

/**
 * Accepted properties for the InlineButton.
 * @public
 */
export interface InlineButtonOwnProps
  extends
    AriaDisabledProps,
    AriaLabelingProps,
    StylingProps,
    DataTestId,
    MaskingProps,
    BehaviorTrackingProps,
    WithChildren {
  /** Called when the button is clicked. Not invoked while the button is disabled or loading. */
  onClick?: MouseEventHandler;

  /**
   * If a button is disabled, it cannot be interacted with: it leaves the tab order
   * and its pointer and click events are blocked.
   * @defaultValue false
   */
  disabled?: boolean;

  /**
   * The text, icon, and underline/hover color.
   * @defaultValue 'neutral'
   */
  color?: 'primary' | 'neutral' | 'success' | 'warning' | 'critical';

  /**
   * The current loading state of the button. If true, a loading icon is shown with aria-disabled=true
   * and aria-busy=true. The button stays in the tab order unless `disabled` is also set.
   * @defaultValue false
   */
  loading?: boolean;

  /** Indicates whether the element, or another grouping element it controls, is currently expanded or collapsed. */
  'aria-expanded'?: boolean | 'true' | 'false';

  /** Indicates the availability and type of interactive popup element, such as menu or dialog, that can be triggered by an element. */
  'aria-haspopup'?:
    | boolean
    | 'menu'
    | 'listbox'
    | 'tree'
    | 'grid'
    | 'dialog'
    | 'true'
    | 'false';

  /** Identifies the element (or elements) whose contents or presence are controlled by the current element. */
  'aria-controls'?: string;
}

/**
 * Props for InlineButton combined with the props of the underlying element type.
 * @public
 */
export type InlineButtonProps<E extends ElementType> =
  PolymorphicComponentProps<E, InlineButtonOwnProps>;

const _InlineButton: <E extends ElementType = 'span'>(
  props: InlineButtonProps<E>,
) => ReactElement | null = /* @__PURE__ */ forwardRef(
  <E extends ElementType>(
    props: InlineButtonProps<E>,
    forwardedRef: typeof props.ref,
  ) => {
    const {
      children,
      onClick,
      disabled,
      color = 'neutral',
      loading = false,
      'aria-disabled': ariaDisabledProp,
      className: consumerClassName,
      style: consumerStyle,
      'data-testid': dataTestId,
      'data-dtrum-mask': dataDtrumMask,
      'data-dtrum-allow': dataDtrumAllow,
      'data-dt-component': dataDtComponent = COMPONENT_NAME,
      as,
      ...remainingProps
    }: InlineButtonProps<E> = props;
    const [slots, label] = useSlots(children, {
      prefix: Prefix,
      suffix: Suffix,
    });

    const [behavioralTrackingProps, restProps] =
      useBehavioralTrackingProps(remainingProps);
    const ariaLabelingProps = useAriaLabelingProps(restProps);
    const ariaDisabled = parseBoolean(ariaDisabledProp);
    const nativeDisabledOrLoading = loading ? true : disabled;
    const isVisuallyDisabled = nativeDisabledOrLoading || ariaDisabled;
    const isTrulyDisabled = disabled || ariaDisabled;

    const isRemovedFromTabOrder = !!disabled;
    const tabIndex = isRemovedFromTabOrder ? -1 : 0;

    const [isKeyboardActive, setIsKeyboardActive] = useState(false);
    const { isFocusVisible, focusProps } = useFocusRing({
      disabled: isVisuallyDisabled,
    });

    const isOpen =
      (restProps as Record<string, unknown>)['data-state'] === 'open';

    const isPopupTrigger =
      props['aria-haspopup'] !== undefined &&
      props['aria-haspopup'] !== false &&
      props['aria-haspopup'] !== 'false';

    // Native <button> elements have their own click semantics for Enter/Space, so
    // the polyfilled keyboard handlers that call .click() are only needed for non-button tags.
    const isNativeButton = (as ?? 'span') === 'button';

    const handleClick: MouseEventHandler<HTMLElement> = (event) => {
      if (nativeDisabledOrLoading) {
        event.preventDefault();
        event.stopPropagation();
        return;
      }
      onClick?.(event);
    };

    // Popup triggers like Menu.Trigger open on pointerdown, so blocking onClick
    // alone wouldn't prevent them from firing while disabled/loading. Also stops
    // pointer events from reaching ancestor listeners.
    const handlePointerEvent: PointerEventHandler<HTMLElement> = (event) => {
      if (nativeDisabledOrLoading) {
        event.preventDefault();
        event.stopPropagation();
      }
    };

    const handleKeyDown: KeyboardEventHandler<HTMLElement> = (event) => {
      if (event.key !== ' ' && event.key !== 'Enter') {
        return;
      }
      if (nativeDisabledOrLoading) {
        event.preventDefault();
        event.stopPropagation();
        return;
      }
      if (!ariaDisabled) {
        setIsKeyboardActive(true);
      }
      if (isPopupTrigger) {
        // Composed inside a Radix popup trigger (Menu.Trigger, etc. via
        // asChild/Slot) — its chained handler respects defaultPrevented, so
        // skip preventDefault here to let it open the popup. Radix prevents
        // the Space page-scroll itself.
        return;
      }
      if (isNativeButton) {
        // A real <button> fires click on Enter/Space itself as part of its
        // native default action (Enter on keydown, Space on keyup) — calling
        // preventDefault() here would suppress that default action entirely.
        // Since we also intentionally skip the manual .click() call below for
        // native buttons, calling preventDefault() unconditionally would leave
        // no code path that ever fires a click.
        return;
      }
      // Space here would otherwise scroll the page; Enter has no default
      // action to prevent on non-native elements, so this call is a
      // harmless no-op for Enter, kept for a single shared code path.
      event.preventDefault();
      if (event.key === 'Enter') {
        event.currentTarget.click();
      }
    };

    const handleKeyUp: KeyboardEventHandler<HTMLElement> = (event) => {
      if (event.key !== ' ' && event.key !== 'Enter') {
        return;
      }
      setIsKeyboardActive(false);
      if (event.key !== ' ') {
        return; // Enter already fired click on keydown
      }
      if (nativeDisabledOrLoading) {
        event.preventDefault();
        event.stopPropagation();
        return;
      }
      if (isPopupTrigger || isNativeButton) {
        return;
      }
      event.preventDefault();
      event.currentTarget.click();
    };

    const Component = as ?? 'span';

    const intl = useIntl();
    return (
      <Component
        ref={forwardedRef}
        // Redundant on a native <button>; required for all other tags.
        role="button"
        tabIndex={tabIndex}
        aria-disabled={loading || disabled ? true : ariaDisabledProp}
        aria-busy={loading}
        // React handlers don't block native form submission; the native attribute does.
        {...(isNativeButton && disabled ? { disabled: true } : {})}
        className={clsx(
          consumerClassName,
          styles.outerButton({
            color,
            visuallyDisabled: isVisuallyDisabled,
          }),
          { [styles.keyboardActive]: isKeyboardActive },
          { [styles.openState]: isOpen },
        )}
        style={consumerStyle}
        // Our handlers must come first in this merge: Radix's own trigger handlers
        // (arriving via restProps) respect event.preventDefault(), so calling
        // it here (when disabled) suppresses Radix's injected open-menu logic too.
        // Reversing this order would silently break disabled + Menu.Trigger composition.
        {...mergeProps(
          {
            'data-testid': dataTestId,
            'data-dtrum-mask': dataDtrumMask,
            'data-dtrum-allow': dataDtrumAllow,
            ...ariaLabelingProps,
            onClick: handleClick,
            onPointerDown: handlePointerEvent,
            onPointerUp: handlePointerEvent,
            onKeyDown: handleKeyDown,
            onKeyUp: handleKeyUp,
            onBlur: () => setIsKeyboardActive(false),
          },
          focusProps,
          // Need to cast: restProps inference is too complex with the polymorphic E to spread directly.
          restProps as Record<string, unknown>,
          behavioralTrackingProps,
        )}
        data-dt-component={dataDtComponent}
      >
        <span
          className={styles.innerChrome({
            visuallyDisabled: isVisuallyDisabled,
            loadingText: loading && !isTrulyDisabled,
            iconOnly: label.length === 0,
            hasPrefix: Boolean(slots.prefix),
            hasSuffix: Boolean(slots.suffix),
            keyboardFocused: isFocusVisible,
          })}
        >
          {slots.prefix}
          {label}
          {slots.suffix}
          {loading && (
            <span className={styles.loadingSpinner}>
              <ProgressCircle
                size="small"
                color={color}
                aria-label={intl.formatMessage({
                  defaultMessage: 'Loading',
                  id: 'cycz+bYG+VH7Of4e',
                  description: 'Label for the loading state.',
                })}
              />
            </span>
          )}
        </span>
      </Component>
    );
  },
);

(_InlineButton as typeof _InlineButton & { displayName: string }).displayName =
  COMPONENT_NAME;

/**
 * InlineButton is an inline, text-flowing action trigger. It flows with surrounding
 * text, wraps naturally across line breaks, and supports `color`, `loading`, and
 * `disabled`/`aria-disabled` states. Use the `as` prop to render a different underlying element.
 * @public
 */
export const InlineButton = Object.assign(_InlineButton, {
  Prefix,
  Suffix,
});
